import React, { type Dispatch, type SetStateAction } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { getDepopIntegrations } from "src/store/plaidIntegration/selector";
import {
  getDepopIntegration,
  depopIntegrationCreate,
} from "src/apiService/modules/depop";
import { getDepopUser } from "src/utils/extension/depop";
import {
  MissingExtensionError,
  OutdatedExtensionError,
} from "src/utils/extension";
import { useRecordEvent } from "src/hooks/useRecordEvent";

import type { DialogState } from "../types";
import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";

import Row, { DepopNoticeNode } from "./Row";

interface DepopProps {
  hideInventory: boolean;
  setDialog: Dispatch<SetStateAction<DialogState>>;
}

function Depop({ setDialog, hideInventory }: DepopProps) {
  const recordEvent = useRecordEvent();
  const depopIntegrations = useSelector(getDepopIntegrations);

  const handleDepopAdd = async () => {
    try {
      const data = await getDepopUser();
      setDialog({
        type: "depop-confirm",
        props: {
          username: data.username,
          platform: "Depop",
          onConfirm: async (bankAccountId?: string | undefined) => {
            if (data?.cookies) {
              const existingDepopIntegration = await getDepopIntegration();
              if (existingDepopIntegration?.id) {
                toast.info("That Depop account is already connected.");
                return;
              }
              await depopIntegrationCreate({
                cookies: data.cookies,
                username: data.username,
                userId: data.userId,
                lastSync: new Date().toISOString(),
                bankAccountId,
              });
              recordEvent({
                type: "feature-adoption",
                feature: "integration-added",
                element: "sale-platform-depop",
              });
            }
            setDialog({});
          },
        },
      });
    } catch (e) {
      if (e instanceof MissingExtensionError) {
        setDialog({
          type: "depop-add-extension",
          props: { platform: "Depop" },
        });
        return;
      }
      if (e instanceof OutdatedExtensionError) {
        setDialog({
          type: "depop-add-extension",
          props: { platform: "Depop", update: true },
        });
        return;
      }
      toast.error((e as Error).message);
    }
  };

  if (depopIntegrations.length === 0)
    return (
      <PlatformIntegrationsTableRow
        name="Depop"
        platform={DepopNoticeNode}
        isRunning={false}
        sync={false}
        inventory={false}
        onImport={handleDepopAdd}
        hideInventory={hideInventory}
        onPullDataNow={async () => handleDepopAdd()}
        onSyncChanged={handleDepopAdd}
        onAdd={handleDepopAdd}
      />
    );

  return (
    <>
      {depopIntegrations.map((integration) => (
        <Row
          key={integration.id}
          integration={integration}
          hideInventory={hideInventory}
          setDialog={setDialog}
        />
      ))}
    </>
  );
}

export default Depop;
